import { format } from 'date-fns';

/**
 * A calendar day: no time, no zone. Wire and storage format is `YYYY-MM-DD`.
 *
 * Step start/due dates are days, not instants. Storing them as instants is what made one step
 * render on two different days for two viewers — see PRO-1069. Never call `new Date(plainDate)`:
 * that parses as UTC midnight and then reads back in the viewer's zone, which is the bug.
 */
export type PlainDate = string & { readonly __plainDate: unique symbol };

const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

/**
 * Builds a UTC instant for a calendar day without going through `Date.UTC` or the `new
 * Date(year, month, day, ...)` constructor form: both treat a year in `0-99` as `1900 + year`
 * (`Date.UTC(2, 1, 20)` is `1902-02-20`, not `0002-02-20`). Routing every year through
 * `setUTCFullYear` after construction sidesteps that mapping entirely.
 */
const utcAt = (year: number, month: number, day: number): Date => {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(0, 0, 0, 0);

  return date;
};

/** Local-civil-date equivalent of `utcAt`, for the same two-digit-year reason. */
const localAt = (year: number, month: number, day: number): Date => {
  const date = new Date(2000, 0, 1);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  return date;
};

/**
 * What the SERVER can actually hand a reader for a step date.
 *
 * The brand guards the type system, but entities are hydrated with `Object.assign` from raw JSON
 * and no reviver validates anything: an un-migrated row, a row the migration marked
 * `unconvertible` or `skippedConcurrentEdit`, or a payload cached before this release all reach
 * the client as a legacy ISO instant behind a `PlainDate` annotation. Every function below that
 * reads a stored date accepts this union and normalises it, so a mid-migration deploy cannot
 * collapse the Gantt to a blank range via `Invalid Date` -> `NaN`.
 */
export type StoredPlainDate = PlainDate | string;

/**
 * Normalise a stored value into a `PlainDate`, or fail loudly and diagnosably.
 *
 * `field` names the argument that carried the bad value, so a broken read edge is identifiable
 * instead of silently producing `Invalid Date` and then `NaN`.
 */
const requirePlainDate = (value: StoredPlainDate, field: string): PlainDate => {
  const normalised = typeof value === 'string' ? plainDateFromLegacy(value) : null;

  if (!normalised) {
    throw new Error(
      `${field}: expected a calendar day (YYYY-MM-DD) or a legacy instant, received ${JSON.stringify(
        value,
      )}`,
    );
  }

  return normalised;
};

const parts = (value: StoredPlainDate, field: string) =>
  requirePlainDate(value, field).split('-').map(Number) as [number, number, number];

/** Shape AND validity: `2026-02-30` matches the pattern but is not a day. */
export const isPlainDate = (value: unknown): value is PlainDate => {
  if (typeof value !== 'string' || !PLAIN_DATE.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const probe = utcAt(year, month, day);

  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

/**
 * The validating boundary: mints a `PlainDate` from raw user input (e.g. `<input type="date">`).
 * Throws on anything that isn't a real calendar day in `YYYY-MM-DD` — including a full instant
 * string, which is exactly the shape this type exists to keep out. Callers on a UI boundary that
 * can receive an empty or cleared value should guard with `isPlainDate` first rather than call
 * this unconditionally.
 */
export const asPlainDate = (value: string): PlainDate => {
  if (!isPlainDate(value)) {
    throw new Error(`Expected a calendar day in YYYY-MM-DD format, received "${value}"`);
  }

  return value;
};

/** Days since the epoch, computed in UTC so neither zone nor DST can reach the arithmetic. */
const toEpochDay = (value: StoredPlainDate, field: string): number => {
  const [year, month, day] = parts(value, field);

  return utcAt(year, month, day).getTime() / MS_PER_DAY;
};

const fromEpochDay = (epochDay: number): PlainDate =>
  new Date(epochDay * MS_PER_DAY).toISOString().slice(0, 10) as PlainDate;

/**
 * `YYYY-MM-DD` from numeric parts, without going near `Date`'s two-digit-year mapping.
 *
 * Guards against a non-finite part reaching `padStart`: `String(NaN).padStart(4, '0')` is
 * `"0NaN"`, which then throws several frames later, out of `requirePlainDate`, with a message
 * naming the wrong function. Every caller of `fmt` is internal to this module, so this is the
 * one place that has to catch it for all of them.
 */
const fmt = (year: number, month: number, day: number): PlainDate => {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    throw new Error(
      `fmt(year, month, day): expected finite numbers, received ${JSON.stringify({
        year,
        month,
        day,
      })}`,
    );
  }

  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-') as PlainDate;
};

/**
 * Days since 1970-01-01. The model layer of the Gantt works in these integers exclusively: they
 * carry no zone, no time and no DST, so no arithmetic on them can be viewer-dependent.
 */
export const epochDayOf = (value: StoredPlainDate): number =>
  toEpochDay(value, 'epochDayOf(value)');

export const plainDateFromEpochDay = (epochDay: number): PlainDate => fromEpochDay(epochDay);

/**
 * 0 = Sunday … 6 = Saturday. Numeric, so weekend detection never depends on a locale's
 * weekday abbreviations the way `toLocaleString('default', { weekday: 'short' })` does.
 * Epoch day 0 (1970-01-01) was a Thursday, hence the +11 before the modulo — which also keeps
 * the result non-negative for days before the epoch, where `%` in JS yields a negative.
 */
export const dayOfWeekOfEpochDay = (epochDay: number): number => ((epochDay % 7) + 11) % 7;

export const dayOfWeek = (value: StoredPlainDate): number =>
  dayOfWeekOfEpochDay(toEpochDay(value, 'dayOfWeek(value)'));

/** `month` is 1-12. Day 0 of the following month is the last day of this one. */
export const daysInMonth = (year: number, month: number): number =>
  utcAt(year, month + 1, 0).getUTCDate();

/**
 * Calendar-month arithmetic with day clamping: Jan 31 + 1 month is Feb 28, never Mar 3.
 * Computed on a year*12+month ordinal rather than by mutating a `Date`, so there is no
 * intermediate overflowed value for a later `setDate` to have to correct.
 */
export const addMonths = (value: StoredPlainDate, months: number): PlainDate => {
  if (!Number.isFinite(months)) {
    throw new Error(
      `addMonths(months): expected a finite number, received ${JSON.stringify(months)}`,
    );
  }

  const [year, month, day] = parts(value, 'addMonths(value)');
  const ordinal = year * 12 + (month - 1) + months;
  const nextYear = Math.floor(ordinal / 12);
  const nextMonth = (((ordinal % 12) + 12) % 12) + 1;

  return fmt(nextYear, nextMonth, Math.min(day, daysInMonth(nextYear, nextMonth)));
};

export const startOfMonth = (value: StoredPlainDate): PlainDate => {
  const [year, month] = parts(value, 'startOfMonth(value)');

  return fmt(year, month, 1);
};

export const endOfMonth = (value: StoredPlainDate): PlainDate => {
  const [year, month] = parts(value, 'endOfMonth(value)');

  return fmt(year, month, daysInMonth(year, month));
};

export const addDays = (value: StoredPlainDate, days: number): PlainDate =>
  fromEpochDay(toEpochDay(value, 'addDays(value)') + days);

export const diffInDays = (from: StoredPlainDate, to: StoredPlainDate): number =>
  toEpochDay(to, 'diffInDays(to)') - toEpochDay(from, 'diffInDays(from)');

/** `YYYY-MM-DD` is lexicographically ordered, so ordering is a string comparison — once both
 * sides are known to BE calendar days. A stored instant sorts after the same day in plain form
 * (`2026-08-25T00:00:00.000Z` > `2026-08-25`), so both sides are normalised first. */
export const comparePlainDates = (a: StoredPlainDate, b: StoredPlainDate): number => {
  const left = requirePlainDate(a, 'comparePlainDates(a)');
  const right = requirePlainDate(b, 'comparePlainDates(b)');

  return left < right ? -1 : left > right ? 1 : 0;
};

/**
 * A JS Date at LOCAL midnight — the ONLY Date form allowed to cross into the Gantt.
 *
 * Local midnight is a sound civil-date carrier: ECMA-262 maps a skipped local time forward, so a
 * zone whose DST transition lands on midnight still reports the requested day from `getDate()`.
 * Verified across America/Santiago, America/Havana, America/Sao_Paulo, Asia/Beirut, Africa/Cairo
 * and Asia/Amman for every day 2010-2035, and across 7000 consecutive `setDate(getDate() + 1)`
 * steps. That invariant is what makes the Gantt's existing local-zone arithmetic correct.
 */
export const toLocalDate = (value: StoredPlainDate): Date => {
  const [year, month, day] = parts(value, 'toLocalDate(value)');

  return localAt(year, month, day);
};

/** Inverse of `toLocalDate`. Reads the LOCAL components — never `toISOString`, which is UTC. */
export const fromLocalDate = (date: Date): PlainDate =>
  [
    String(date.getFullYear()).padStart(4, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-') as PlainDate;

export const todayPlainDate = (): PlainDate => fromLocalDate(new Date());

/**
 * A `PlainDate` rendered as the legacy local-noon instant that Task and Project still expect on
 * the wire. Those two entities keep instant semantics and have their own follow-up ticket; their
 * API paths (`dateInputToLuxon` for Task, `new Date(input.startDate)` for Project) parse a bare
 * `YYYY-MM-DD` as SERVER-zone midnight, which reads back a day early for every viewer west of
 * UTC — the exact PRO-1069 defect. Local noon leaves twelve hours of slack on either side, so the
 * stored instant still lands on the intended calendar day when it is read back.
 *
 * Built from `toLocalDate` plus native `Date` rather than Luxon: `toLocalDate` is this module's
 * already-DST-verified civil-day carrier, so no second date library has to be trusted with the
 * same invariant. The old modal produced `DateTime.fromFormat(`${date} 12:00`, 'yyyy-MM-dd hh:mm')
 * .toISO()` — the same instant, printed with a zone offset instead of as UTC; both API paths parse
 * either form identically (`DateTime.fromISO` / `new Date`).
 *
 * Delete this when Task and Project migrate to `PlainDate`.
 */
export const toLegacyInstant = (value: StoredPlainDate): string => {
  const localNoon = toLocalDate(value);
  localNoon.setHours(12, 0, 0, 0);

  return localNoon.toISOString();
};

export const formatPlainDate = (value: StoredPlainDate, pattern: string): string =>
  format(toLocalDate(value), pattern);

/**
 * The calendar day a legacy instant was written to MEAN. Needed only while un-migrated data or a
 * stale cached payload can still reach the client; delete it with the API shim.
 *
 * Every reader above routes through this, so it is the single place the legacy shape is decoded.
 */
export const plainDateFromLegacy = (value: string): PlainDate | null => {
  if (isPlainDate(value)) return value;

  const match = value.match(/^(\d{4}-\d{2}-\d{2})T/);

  return match && isPlainDate(match[1]) ? (match[1] as PlainDate) : null;
};
